"use strict";
/**
 * Meci - Native Agora Voice Service for Electron
 * Provides high-performance communication using native binaries.
 * On Linux, gracefully falls back to WebRTC if Agora SDK is unavailable.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.nativeVoiceService = void 0;
// Dynamically import agora-electron-sdk only if it's available (not on Linux/unsupported platforms)
let createAgoraRtcEngine = null;
let ChannelProfileType = null;
let ClientRoleType = null;
const platform = typeof process !== 'undefined' ? process.platform : 'web';
if ((platform === 'win32' || platform === 'darwin') && typeof window !== 'undefined' && window.require) {
    try {
        const agora = window.require('agora-electron-sdk');
        createAgoraRtcEngine = agora.createAgoraRtcEngine;
        ChannelProfileType = agora.ChannelProfileType;
        ClientRoleType = agora.ClientRoleType;
        console.log('[NativeVoiceService] Agora SDK loaded successfully for platform:', platform);
    }
    catch (e) {
        console.warn('[NativeVoiceService] Agora SDK not available, falling back to WebRTC:', e);
    }
}
else {
    console.log('[NativeVoiceService] Non-Electron or unsupported platform detected, Agora native SDK disabled. Using WebRTC fallback.');
}
class NativeVoiceService {
    constructor() {
        this.engine = null;
        this.isInitialized = false;
        this.isNativeAvailable = !!(createAgoraRtcEngine);
        this.initEngine();
    }
    initEngine() {
        if (!createAgoraRtcEngine) {
            console.log('[NativeVoiceService] Native engine not available on this platform.');
            return;
        }
        try {
            this.engine = createAgoraRtcEngine();
            this.isInitialized = false;
            console.log('[NativeVoiceService] Engine created');
        }
        catch (e) {
            console.error('[NativeVoiceService] Failed to create engine:', e);
        }
    }
    async initialize(appId, onEvent) {
        if (!this.engine)
            return false;
        if (this.isInitialized)
            return true;
        try {
            this.engine.initialize({
                appId: appId,
                channelProfile: ChannelProfileType?.ChannelProfileCommunication ?? 0,
            });
            // Register events
            if (onEvent) {
                this.engine.registerEventHandler({
                    onJoinChannelSuccess: (connection, elapsed) => {
                        console.log('[NativeVoiceService] onJoinChannelSuccess', connection);
                        onEvent('connection-state-change', 'connected', 'disconnected', 'JOIN_SUCCESS');
                    },
                    onUserJoined: (connection, remoteUid, elapsed) => {
                        console.log('[NativeVoiceService] onUserJoined', remoteUid);
                        onEvent('user-published', { uid: remoteUid, hasAudio: true, hasVideo: false }, 'audio');
                    },
                    onUserOffline: (connection, remoteUid, reason) => {
                        console.log('[NativeVoiceService] onUserOffline', remoteUid);
                        onEvent('user-unpublished', { uid: remoteUid }, 'audio');
                    },
                    onLeaveChannel: (connection, stats) => {
                        onEvent('connection-state-change', 'disconnected', 'connected', 'LEAVE_CHANNEL');
                    }
                });
            }
            this.engine.enableAudio();
            this.engine.enableVideo();
            this.isInitialized = true;
            console.log('[NativeVoiceService] Engine initialized with App ID:', appId);
            return true;
        }
        catch (e) {
            console.error('[NativeVoiceService] Initialization failed:', e);
            return false;
        }
    }
    async setVideoEnabled(enabled) {
        if (!this.engine)
            return;
        if (enabled) {
            this.engine.enableLocalVideo(true);
            this.engine.startPreview();
        }
        else {
            this.engine.stopPreview();
            this.engine.enableLocalVideo(false);
        }
    }
    async setMute(muted) {
        if (!this.engine)
            return;
        try {
            this.engine.muteLocalAudioStream(muted);
        }
        catch (e) {
            console.error('[NativeVoiceService] setMute failed:', e);
        }
    }
    async setDeafen(deafened) {
        if (!this.engine)
            return;
        try {
            this.engine.muteAllRemoteAudioStreams(deafened);
            this.engine.muteLocalAudioStream(deafened);
        }
        catch (e) {
            console.error('[NativeVoiceService] setDeafen failed:', e);
        }
    }
    async startScreenShare(sourceId) {
        if (!this.engine)
            return;
        if (sourceId.startsWith('screen:')) {
            const displayId = parseInt(sourceId.replace('screen:', ''));
            this.engine.startScreenCaptureByDisplayId(displayId, {
                x: 0, y: 0, width: 1920, height: 1080
            }, {
                dimensions: { width: 1920, height: 1080 },
                frameRate: 15,
                bitrate: 0,
                captureMouseCursor: true,
                windowFocus: true
            });
        }
        else {
            const windowId = parseInt(sourceId);
            this.engine.startScreenCaptureByWindowId(windowId, {
                x: 0, y: 0, width: 0, height: 0
            }, {
                dimensions: { width: 1920, height: 1080 },
                frameRate: 15,
                bitrate: 0,
                captureMouseCursor: true,
                windowFocus: true
            });
        }
    }
    stopScreenShare() {
        if (!this.engine)
            return;
        try {
            this.engine.stopScreenCapture();
            console.log('[NativeVoiceService] Screen share stopped');
        }
        catch (e) {
            console.error('[NativeVoiceService] stopScreenCapture failed:', e);
        }
    }
    async join(channelName, token, uid) {
        if (!this.engine || !this.isInitialized)
            return false;
        try {
            this.engine.setClientRole(ClientRoleType?.ClientRoleBroadcaster ?? 1);
            this.engine.joinChannel(token, channelName, uid, {});
            console.log('[NativeVoiceService] Joined channel:', channelName);
            return true;
        }
        catch (e) {
            console.error('[NativeVoiceService] Join failed:', e);
            return false;
        }
    }
    async leave() {
        if (!this.engine)
            return;
        try {
            this.engine.leaveChannel();
            console.log('[NativeVoiceService] Left channel');
        }
        catch (e) {
            console.error('[NativeVoiceService] Leave failed:', e);
        }
    }
    release() {
        if (this.engine) {
            this.engine.release();
            this.engine = null;
            this.isInitialized = false;
        }
    }
}
exports.nativeVoiceService = new NativeVoiceService();
